import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Clock, Calendar, Heart } from "lucide-react";
import { getPost } from "@/lib/api";
import DonateDialog from "@/components/DonateDialog";
import { Reveal } from "@/components/Reveal";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.4], [0, 100]);

  useEffect(() => {
    setPost(null); setError(false);
    getPost(slug).then(setPost).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="pt-40 pb-32 text-center" data-testid="post-not-found">
      <p className="text-stone">This story could not be found.</p>
      <Link to="/blog" className="text-plum font-medium mt-4 inline-block border-b-2 border-gold">Back to Blog</Link>
    </div>
  );

  if (!post) return <div className="pt-48 pb-32 text-center text-stone" data-testid="post-loading">Loading...</div>;

  return (
    <article data-testid="blog-post">
      <section className="relative pt-40 pb-20 sm:pt-52 sm:pb-28 overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-plum-dark/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-transparent" />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-cream/80 hover:text-gold text-sm mb-6" data-testid="post-back"><ArrowLeft size={16} /> All Stories</Link>
          <span className="text-gold-light text-xs tracking-[0.2em] uppercase font-semibold block">{post.category}</span>
          <h1 className="font-heading text-cream text-4xl sm:text-5xl lg:text-6xl font-semibold mt-3 leading-[1.03] text-balance">{post.title}</h1>
          <div className="flex items-center gap-5 text-sm text-cream/80 mt-6">
            <span className="flex items-center gap-1.5"><Calendar size={15} />{fmtDate(post.date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={15} />{post.read_minutes} min read</span>
            <span>By {post.author}</span>
          </div>
        </div>
      </section>

      <section className="py-16 relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading text-2xl text-plum leading-snug mb-8 italic">{post.excerpt}</p>
            <div className="space-y-6">
              {post.body.split("\n\n").map((para, i) => (
                <p key={i} className="text-ink/85 leading-[1.85] text-lg">{para}</p>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-14 bg-plum rounded-2xl p-8 sm:p-10 text-center">
            <Heart className="text-gold mx-auto mb-4" size={32} />
            <h3 className="font-heading text-2xl sm:text-3xl text-cream font-semibold">Be part of the next story</h3>
            <p className="text-cream/75 mt-3">Your gift keeps this work going. Give today and change a life.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-6">
              <DonateDialog trigger={<button className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="post-donate-btn"><Heart size={17} /> Donate</button>} />
              <Link to="/get-involved" className="border border-cream/40 text-cream rounded-full px-7 py-3.5 hover:bg-cream/10 transition-colors" data-testid="post-involved-btn">Get Involved</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
